import {
  GraphicComponent,
  IGraphicsControllerStoreState,
  IGraphicSetting,
  IPoint,
  PlacementSetting,
  PlacementTypes,
  IGraphicUGCStoreItem,
  IGraphicUGCItem,
  IZoomGraphicUGCItem,
  IGraphic
} from "@/libs/graphics/types/Graphics.t";
import { MutationTree } from "vuex";
import { MutationTypesGraphicsController } from "./types";

export interface PlacementSettingPayload {
  componentSetting: GraphicComponent;
  placementComponent: PlacementTypes;
  type: "user_defined" | "default";
}

export interface IAddOrUpdateGraphicUgcItemPayload {
  placement: PlacementTypes;
  graphicUgcItem: IGraphicUGCItem;
}

export interface IRemoveGraphicUgcItem {
  placement: PlacementTypes;
}

export interface IAddGraphicUgcStoreItemPayload {
  placement: PlacementTypes;
  graphicUgcItem: IGraphicUGCItem;
}

export interface IOutOfBoundsModalInfoPayload {
  activeComponent: PlacementTypes;
  graphicId: string;
}

export interface IComponentGraphicPayload {
  component: PlacementTypes;
  graphic: IGraphic;
  graphicViewZoomBox: IZoomGraphicUGCItem;
}

export type MutationGraphicsController<S = IGraphicsControllerStoreState> = {
  [MutationTypesGraphicsController.SET_GRAPHICS_SETTINGS](
    state: IGraphicsControllerStoreState,
    payload: IGraphicSetting[]
  ): void;

  [MutationTypesGraphicsController.SET_EDITOR_VISIBLE](
    state: IGraphicsControllerStoreState,
    payload: boolean
  ): void;

  [MutationTypesGraphicsController.SET_ACTIVE_GRAPHIC_VIEWBOX](
    state: IGraphicsControllerStoreState,
    payload: IPoint
  ): void;

  [MutationTypesGraphicsController.SET_PLACEMENT_SETTING](
    state: IGraphicsControllerStoreState,
    payload: PlacementSettingPayload
  ): void;

  [MutationTypesGraphicsController.ADD_GRAPHIC_UGC_STORE_ITEM](
    state: IGraphicsControllerStoreState,
    payload: IAddGraphicUgcStoreItemPayload
  ): void;

  [MutationTypesGraphicsController.ADD_OR_UPDATE_GRAPHIC_UGC_ITEM](
    state: IGraphicsControllerStoreState,
    payload: IAddOrUpdateGraphicUgcItemPayload
  ): void;

  /**
   * Use this to clear the UGC data after a new image is uploaded so all the previous info like viewbox and dimensions are reset
   * @param state
   * @param payload
   */
  [MutationTypesGraphicsController.CLEAR_UGC_DATA_PER_PLACEMENT](
    state: IGraphicsControllerStoreState,
    payload: IRemoveGraphicUgcItem
  ): void;

  [MutationTypesGraphicsController.ADD_OUT_OF_BOUNDS_MODAL_SHOWN_INFO](
    state: IGraphicsControllerStoreState,
    payload: IOutOfBoundsModalInfoPayload
  ): void;

  [MutationTypesGraphicsController.SET_ORIGINAL_GRAPHIC_VIEWBOX](
    state: IGraphicsControllerStoreState,
    payload: IComponentGraphicPayload
  ): void;

  [MutationTypesGraphicsController.SET_ORIGINAL_GRAPHIC_ZOOM_VIEWBOX](
    state: IGraphicsControllerStoreState,
    payload: IComponentGraphicPayload
  ): void;

  [MutationTypesGraphicsController.CLEAR_PLACEMENT_SETTING](
    state: IGraphicsControllerStoreState
  ): void;
};

export const mutations: MutationTree<IGraphicsControllerStoreState> &
  MutationGraphicsController = {
  [MutationTypesGraphicsController.SET_GRAPHICS_SETTINGS](state, payload) {
    state.graphicSetting = [...payload];
  },

  [MutationTypesGraphicsController.SET_EDITOR_VISIBLE](state, payload) {
    state.editorVisible = payload;
  },

  [MutationTypesGraphicsController.SET_ACTIVE_GRAPHIC_VIEWBOX](state, payload) {
    state.activeGraphicViewbox = payload;
  },

  [MutationTypesGraphicsController.SET_ORIGINAL_GRAPHIC_VIEWBOX](state, payload) {
    state.originalGraphicViewbox = {
      ...state.originalGraphicViewbox,
      [payload.component]: {
        ...state.originalGraphicViewbox[payload.component],
        [payload.graphic.id]: payload.graphicViewZoomBox
      }
    }
  },

  [MutationTypesGraphicsController.SET_ORIGINAL_GRAPHIC_ZOOM_VIEWBOX](state, payload) {

    state.originalGraphicViewbox = {
      ...state.originalGraphicViewbox,
      [payload.component]: {
        ...state.originalGraphicViewbox[payload.component],
        [payload.graphic.id]: {
          ...state.originalGraphicViewbox?.[payload.component]?.[(payload.graphic.id as number)],
          zoomValue: payload.graphicViewZoomBox.zoomValue
        }
      }
    }
  },

  [MutationTypesGraphicsController.SET_PLACEMENT_SETTING](state, payload) {
    let placementItem: PlacementSetting;
    const placementItemIndex = state.placementSetting.findIndex(
      (item) => item.placementComponent === payload.placementComponent
    );
    // the placement item don't exist yet, create it with the component setting
    if (placementItemIndex === -1) {
      placementItem = {
        placementComponent: payload.placementComponent,
        componentSetting: [{ ...payload.componentSetting, type: payload.type }],
      };
      state.placementSetting.push(placementItem);
      return;
    }
    // the placement item exists
    // checks if the component setting exists
    const componentSettingItemIndex = state.placementSetting[
      placementItemIndex
    ].componentSetting.findIndex(
      (item) => item.id === payload.componentSetting.id
    );

    if (componentSettingItemIndex === -1) {
      state.placementSetting[placementItemIndex].componentSetting.push({
        ...payload.componentSetting,
        type: payload.type,
      });
      return;
    }

    if (
      payload.type === "user_defined" ||
      state.placementSetting[placementItemIndex].componentSetting[
        componentSettingItemIndex
      ].type === "default"
    ) {
      // if the component exists and the value should be overwritten
      state.placementSetting[placementItemIndex].componentSetting.splice(
        componentSettingItemIndex,
        1,
        { ...payload.componentSetting, type: payload.type }
      );
    }
  },

  [MutationTypesGraphicsController.ADD_GRAPHIC_UGC_STORE_ITEM](
    state,
    { placement, graphicUgcItem }
  ) {
    state.graphicUGCSetting.push({
      placement,
      graphics: [graphicUgcItem],
    });
  },

  [MutationTypesGraphicsController.ADD_OR_UPDATE_GRAPHIC_UGC_ITEM](
    state,
    { placement, graphicUgcItem }
  ) {
    const storeItemIndex = state.graphicUGCSetting.findIndex(
      (item: IGraphicUGCStoreItem) => item.placement === placement
    );

    // create the Graphic UGC store if it doesn't exist
    if (storeItemIndex === -1) {
      state.graphicUGCSetting.push({
        placement,
        graphics: [graphicUgcItem],
      });
      return;
    }

    const storeItem = state.graphicUGCSetting[storeItemIndex];
    const graphicUgcItemIndex = storeItem.graphics.findIndex(
      (item: IGraphicUGCItem) => item.id === graphicUgcItem.id
    );

    if (graphicUgcItemIndex !== -1) {
      storeItem.graphics[graphicUgcItemIndex] = graphicUgcItem;
    } else {
      storeItem.graphics.push(graphicUgcItem);
    }
  },

  [MutationTypesGraphicsController.CLEAR_UGC_DATA_PER_PLACEMENT](
    state,
    { placement }
  ) {
    const ugcStoreItem = state.graphicUGCSetting.findIndex(
      (item: IGraphicUGCStoreItem) => item.placement === placement
    )
    if (ugcStoreItem > -1) {
      state.graphicUGCSetting[ugcStoreItem].graphics = [];
    }

    delete state.outOfBoundsModalShownInfo[placement];
  },

  [MutationTypesGraphicsController.ADD_OUT_OF_BOUNDS_MODAL_SHOWN_INFO](
    state,
    {activeComponent, graphicId}
  ) {
    state.outOfBoundsModalShownInfo = {
      ...state.outOfBoundsModalShownInfo,
      [activeComponent]: {
        ...state.outOfBoundsModalShownInfo[activeComponent],
        [graphicId]: true
      }
    }
  },

  [ MutationTypesGraphicsController.CLEAR_PLACEMENT_SETTING](state) {
    state.placementSetting = [];
  }
};

export default mutations;
